import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import AppShell from '../components/AppShell';

const ROLE_LABELS = { super_admin: 'Власник', store_admin: 'Адмін магазину', cashier: 'Касир' };

export default function Dashboard() {
  const [stores, setStores] = useState([]);
  const [newStore, setNewStore] = useState({ name: '', address: '', copy_from_store_id: '', copy_stock: false });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuth();

  useEffect(() => {
    if (!user) return;
    api.get('/stores/').then(r => {
      let all = r.data;
      if (!isSuperAdmin) {
        const myStoreIds = (user.store_memberships || []).map(m => m.store_id);
        all = all.filter(s => myStoreIds.includes(s.id));
      }
      setStores(all);
    }).finally(() => setLoading(false));
  }, [user, isSuperAdmin]);

  async function createStore(e) {
    e.preventDefault();
    const payload = {
      name: newStore.name,
      address: newStore.address,
      copy_from_store_id: newStore.copy_from_store_id || null,
      copy_stock: newStore.copy_stock,
    };
    const res = await api.post('/stores/', payload);
    setStores(prev => [...prev, res.data]);
    setNewStore({ name: '', address: '', copy_from_store_id: '', copy_stock: false });
    setShowForm(false);
  }

  const baseStore = stores.find(s => s.is_base);

  async function markBase(e, storeId) {
    e.stopPropagation();
    await api.patch(`/stores/${storeId}`, { is_base: true });
    setStores(prev => prev.map(s => ({ ...s, is_base: s.id === storeId })));
  }

  const menuItems = [
    { icon: '👥', title: 'CRM', desc: 'База клієнтів та бонуси', path: '/crm', cls: 'm-blue' },
    { icon: '🍺', title: 'Асортимент', desc: 'Товари, категорії та коди — спільні на мережу', path: '/assortment', cls: 'm-amber' },
    { icon: '📦', title: 'Постачальники', desc: 'Постачальники та формування замовлень', path: '/suppliers', cls: 'm-green' },
    ...(isSuperAdmin ? [
      { icon: '📊', title: 'Аналітика', desc: 'Залишки, рух товарів, статистика', path: '/analytics', cls: 'm-green' },
      { icon: '⚙️', title: 'Налаштування', desc: 'Бонуси та параметри системи', path: '/settings', cls: 'm-amber' },
    ] : []),
  ];

  return (
    <AppShell>
      <div className="dash-hero">
        <h1>Вітаю, {(user?.full_name || '').split(' ')[0] || 'друже'} 👋</h1>
        <p className="muted">{ROLE_LABELS[user?.role?.toLowerCase()]} · керуй мережею Draft Beer</p>
      </div>

      <section className="dash-section">
        <h2 className="section-title">Управління мережею</h2>
        <div className="grid grid-auto">
          {menuItems.map((item, i) => (
            <div key={i}
              className={`card card-hover menu-card ${item.cls} ${item.soon ? 'is-soon' : ''}`}
              onClick={() => !item.soon && navigate(item.path)}>
              <div className="menu-icon">{item.icon}</div>
              <div className="menu-title">
                {item.title}
                {item.soon && <span className="badge">Незабаром</span>}
              </div>
              <div className="muted menu-desc">{item.desc}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="dash-section">
        <div className="row-between" style={{ marginBottom: 14 }}>
          <h2 className="section-title" style={{ margin: 0 }}>
            {isSuperAdmin ? 'Магазини мережі' : 'Мої магазини'}
          </h2>
          {isSuperAdmin && (
            <button className="btn btn-soft btn-sm" onClick={() => {
              if (!showForm) setNewStore(s => ({ ...s, copy_from_store_id: baseStore?.id || '' }));
              setShowForm(s => !s);
            }}>
              {showForm ? '✕ Скасувати' : '+ Магазин'}
            </button>
          )}
        </div>

        {isSuperAdmin && showForm && (
          <form onSubmit={createStore} className="card store-form">
            <div className="field">
              <label className="label">Назва магазину</label>
              <input placeholder="Draft Beer Центр" value={newStore.name}
                onChange={e => setNewStore({ ...newStore, name: e.target.value })} required />
            </div>
            <div className="field">
              <label className="label">Адреса</label>
              <input placeholder="вул. Головна, 1" value={newStore.address}
                onChange={e => setNewStore({ ...newStore, address: e.target.value })} />
            </div>
            <div className="field">
              <label className="label">Скопіювати асортимент із</label>
              <select value={newStore.copy_from_store_id}
                onChange={e => setNewStore({ ...newStore, copy_from_store_id: e.target.value })}>
                <option value="">— не копіювати (порожній магазин) —</option>
                {stores.map(s => (
                  <option key={s.id} value={s.id}>{s.name}{s.is_base ? ' (база)' : ''}</option>
                ))}
              </select>
            </div>
            {newStore.copy_from_store_id && (
              <label className="row" style={{ gap: 8, alignItems: 'center', marginBottom: 12, cursor: 'pointer' }}>
                <input type="checkbox" checked={newStore.copy_stock}
                  onChange={e => setNewStore({ ...newStore, copy_stock: e.target.checked })} />
                <span className="muted">Скопіювати також залишки (зазвичай ні — новий магазин рахує свої)</span>
              </label>
            )}
            <button className="btn btn-primary" type="submit">Додати</button>
          </form>
        )}

        {loading ? (
          <div className="hint">Завантаження…</div>
        ) : stores.length === 0 ? (
          <div className="card hint">Поки немає магазинів{isSuperAdmin ? ' — додайте перший' : ''}</div>
        ) : (
          <div className="grid grid-auto">
            {stores.map(store => {
              const myRole = isSuperAdmin ? 'super_admin'
                : user.store_memberships?.find(m => m.store_id === store.id)?.role;
              return (
                <div key={store.id} className="card card-hover store-card"
                  onClick={() => navigate(`/store/${store.id}`)}>
                  <div className="store-icon">🏪</div>
                  <h3 className="store-name">
                    {store.name}
                    {store.is_base && <span className="badge badge-amber" style={{ marginLeft: 8 }}>🏠 База</span>}
                  </h3>
                  <p className="muted store-addr">{store.address || 'Адреса не вказана'}</p>
                  <div className="row-between store-foot">
                    <span className="badge badge-amber">{ROLE_LABELS[myRole] || myRole}</span>
                    {isSuperAdmin && !store.is_base && (
                      <button className="btn btn-soft btn-sm" title="Зробити базовим магазином"
                        onClick={e => markBase(e, store.id)}>Зробити базовим</button>
                    )}
                    <span className="amber store-arrow">→</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </AppShell>
  );
}
